import { i18n } from '../i18n';
import { ClientCategory, Roles, User } from '../types';
import { queryFirstMatchingSelector, showToRoles } from '../utils';

type TutorialRoles = Array<Roles | ClientCategory> | 'admin' | 'client';

export interface TutorialButtonTour {
  name: string;
  key: string;
  showToRoles?: TutorialRoles;
  showToScreen?: 'desktop' | 'mobile';
  canTrade?: boolean;
}

export interface TutorialPageConfig {
  NAME: string;
  ROUTE: string | string[];
  DISABLED_FIND_ROUTE?: boolean;
  BUTTON_TOURS: TutorialButtonTour[];
  [tourKey: string]: unknown;
}

interface TourStep {
  element?: string | (() => Element);
  popover?: {
    title: string;
    description: string;
    side?: string;
  };
  onPrevClickEl?: string;
  onNextClickEl?: string;
  showToScreen?: 'desktop' | 'mobile';
  showToRoles?: TutorialRoles;
}

const TUTORIALS_DATA: Record<string, TutorialPageConfig> = {
  PAGE_GUIDE: {
    NAME: i18n.t('tutorial.pageGuideTitle'),
    ROUTE: '/dashboard/containers',
    DISABLED_FIND_ROUTE: true,
    BUTTON_TOURS: [
      {
        name: i18n.t('button.basic'),
        key: 'BASIC_TOUR',
      },
    ],
    BASIC_TOUR: [
      {
        element: '#appbar',
        popover: {
          title: i18n.t('tutorial.tours.pageGuide.basic1.title'),
          description: i18n.t('tutorial.tours.pageGuide.basic1.description'),
        },
      },
      {
        element: '#dashboard-drawer',
        popover: {
          title: i18n.t('tutorial.tours.pageGuide.basic2.title'),
          description: i18n.t('tutorial.tours.pageGuide.basic2.description'),
        },
        showToScreen: 'desktop',
      },
      {
        element: '#appbar-menu-icon',
        popover: {
          title: i18n.t('tutorial.tours.pageGuide.basic2.title'),
          description: i18n.t('tutorial.tours.pageGuide.basic2Mobile.description'),
        },
        showToScreen: 'mobile',
      },
      {
        element: '#dashboard-main',
        popover: {
          title: i18n.t('tutorial.tours.pageGuide.basic3.title'),
          description: i18n.t('tutorial.tours.pageGuide.basic3.description'),
        },
        showToScreen: 'desktop',
      },
      {
        element: '#dashboard-main > *',
        popover: {
          title: i18n.t('tutorial.tours.pageGuide.basic3.title'),
          description: i18n.t('tutorial.tours.pageGuide.basic3.description'),
        },
        showToScreen: 'mobile',
      },
    ],
  },
  BASE_TABLE_GUIDE: {
    NAME: i18n.t('tutorial.baseTableGuideTitle'),
    ROUTE: '/dashboard/containers',
    DISABLED_FIND_ROUTE: true,
    BUTTON_TOURS: [
      {
        name: i18n.t('button.basic'),
        key: 'BASIC_TOUR',
      },
      {
        name: i18n.t('button.containerListButton'),
        key: 'CONTAINER_LIST_TOUR',
      },
      {
        name: i18n.t('label.3dotsActions'),
        key: 'ACTIONS_TOUR',
      },
      {
        name: i18n.t('label.checkboxActions'),
        key: 'CHECKBOX_ACTIONS_TOUR',
      },
    ],
    BASIC_TOUR: [
      {
        element: '#containers-header',
        popover: {
          title: i18n.t('tutorial.tours.baseTableGuide.basic1.title'),
          description: i18n.t('tutorial.tours.baseTableGuide.basic1.description'),
        },
      },
      {
        element: '.table-filters,#filter-icon-button',
        popover: {
          title: i18n.t('tutorial.tours.baseTableGuide.basic2.title'),
          description: i18n.t('tutorial.tours.baseTableGuide.basic2.description'),
        },
      },
      {
        element: 'tbody > .MuiTableRow-root,.table-cards',
        popover: {
          title: i18n.t('tutorial.tours.baseTableGuide.basic3.title'),
          description: i18n.t('tutorial.tours.baseTableGuide.basic3.description'),
        },
      },
    ] as TourStep[],
    CONTAINER_LIST_TOUR: [
      {
        element: '#container-list-button',
        popover: {
          title: i18n.t('tutorial.tours.baseTableGuide.containerList1.title'),
          description: i18n.t('tutorial.tours.baseTableGuide.containerList1.description'),
        },
        onNextClickEl: '#container-list-button',
      },
      {
        element: '#container-list-modal .MuiPaper-root',
        popover: {
          title: i18n.t('tutorial.tours.baseTableGuide.containerList2.title'),
          description: i18n.t('tutorial.tours.baseTableGuide.containerList2.description'),
        },
        onPrevClickEl: '#container-list-modal .MuiCloseIcon',
        onNextClickEl: '#container-list-modal .MuiCloseIcon',
      },
    ] as TourStep[],
    ACTIONS_TOUR: [
      {
        element: '.MuiTableCell-root .MuiIconButton-root,.table-cards .MuiIconButton-root',
        popover: {
          title: i18n.t('tutorial.tours.baseTableGuide.actions1.title'),
          description: i18n.t('tutorial.tours.baseTableGuide.actions1.description'),
        },
        onNextClickEl: '.MuiTableCell-root .MuiIconButton-root,.table-cards .MuiIconButton-root',
      },
      {
        element: '.MuiMenu-paper',
        popover: {
          title: i18n.t('tutorial.tours.baseTableGuide.actions2.title'),
          description: i18n.t('tutorial.tours.baseTableGuide.actions2.description'),
        },
        onPrevClickEl: '.MuiMenu-root .MuiModal-backdrop',
        onNextClickEl: '.MuiMenu-root .MuiModal-backdrop',
      },
    ] as TourStep[],
    CHECKBOX_ACTIONS_TOUR: [
      {
        element: 'tbody .MuiCheckbox-root > input,.MuiCheckbox-root > input',
        popover: {
          title: i18n.t('tutorial.tours.baseTableGuide.checkboxActions1.title'),
          description: i18n.t('tutorial.tours.baseTableGuide.checkboxActions1.description'),
        },
        onNextClickEl: 'tbody .MuiCheckbox-root > input,.MuiCheckbox-root > input',
      },
      {
        element: '.table-checkbox-actions button',
        popover: {
          title: i18n.t('tutorial.tours.baseTableGuide.checkboxActions2.title'),
          description: i18n.t('tutorial.tours.baseTableGuide.checkboxActions2.description'),
        },
        onPrevClickEl: 'tbody .MuiCheckbox-root > input,.MuiCheckbox-root > input',
      },
      {
        element: 'tbody > .MuiTableRow-root,.table-cards > div',
        popover: {
          title: i18n.t('tutorial.tours.baseTableGuide.checkboxActions3.title'),
          description: i18n.t('tutorial.tours.baseTableGuide.checkboxActions3.description'),
        },
        onNextClickEl: 'tbody .MuiCheckbox-root > input,.MuiCheckbox-root > input',
      },
    ] as TourStep[],
  },
  OVERVIEW: {
    NAME: i18n.t('button.overview'),
    ROUTE: '/dashboard/overview',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.basic'),
        key: 'BASIC_TOUR',
        showToRoles: [
          'SUPER_ADMIN',
          'YARD_COMPANY_ADMIN',
          'YARD_COMPANY_EMPLOYEE',
          'YARD_MANAGER',
          'YARD_DISPATCHER',
          'VENDOR',
        ],
      },
    ],
    BASIC_TOUR: [
      {
        element: '.appbar-inputs',
        popover: {
          title: i18n.t('tutorial.tours.overview.basic1.title'),
          description: i18n.t('tutorial.tours.overview.basic1.description'),
        },
        showToScreen: 'desktop',
        showToRoles: ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'],
      },
      {
        element: '#overview-page > *:nth-child(1)',
        popover: {
          title: i18n.t('tutorial.tours.overview.basic1.title'),
          description: i18n.t('tutorial.tours.overview.basic1.description'),
        },
        showToScreen: 'mobile',
        showToRoles: ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'],
      },
      {
        element: '.appbar-inputs',
        popover: {
          title: i18n.t('tutorial.tours.overview.basic1.title1'),
          description: i18n.t('tutorial.tours.overview.basic1.description1'),
        },
        showToScreen: 'desktop',
        showToRoles: ['YARD_MANAGER', 'YARD_DISPATCHER', 'VENDOR'],
      },
      {
        element: '#overview-page > *:nth-child(1)',
        popover: {
          title: i18n.t('tutorial.tours.overview.basic1.title1'),
          description: i18n.t('tutorial.tours.overview.basic1.description1'),
        },
        showToScreen: 'mobile',
        showToRoles: ['YARD_MANAGER', 'YARD_DISPATCHER', 'VENDOR'],
      },
      {
        element: '#overview-page',
        popover: {
          title: i18n.t('tutorial.tours.overview.basic2.title'),
          description: i18n.t('tutorial.tours.overview.basic2.description'),
        },
      },
    ],
  },
  CONTAINERS: {
    NAME: i18n.t('pages.containers.title'),
    ROUTE: '/dashboard/containers',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.containerEnterExitWithPreviousTiming'),
        key: 'OLD_GATE_IN_OUT_TOUR',
        showToRoles: 'admin',
      },
      {
        name: i18n.t('button.addToRepair'),
        key: 'ADD_TO_REPAIR_TOUR',
        showToRoles: 'admin',
      },
      {
        name: i18n.t('button.3dViewContainer'),
        key: '3D_VIEW_CONTAINER_TOUR',
      },
    ],
    OLD_GATE_IN_OUT_TOUR: [
      {
        element: '#gate-in-container-old',
        popover: {
          title: i18n.t('tutorial.tours.containers.oldGateInOut1.title'),
          description: i18n.t('tutorial.tours.containers.oldGateInOut1.description'),
        },
      },
      {
        element: '#gate-out-container-old',
        popover: {
          title: i18n.t('tutorial.tours.containers.oldGateInOut2.title'),
          description: i18n.t('tutorial.tours.containers.oldGateInOut2.description'),
        },
      },
    ],
    ADD_TO_REPAIR_TOUR: [
      {
        element: '.MuiTableCell-root .MuiIconButton-root',
        popover: {
          title: i18n.t('tutorial.tours.containers.addToRepair1.title'),
          description: i18n.t('tutorial.tours.containers.addToRepair1.description'),
        },
        onNextClickEl: '.MuiTableCell-root .MuiIconButton-root',
      },
      {
        element: '.MuiMenu-paper',
        popover: {
          title: i18n.t('tutorial.tours.containers.addToRepair2.title'),
          description: i18n.t('tutorial.tours.containers.addToRepair2.description'),
        },
        onPrevClickEl: '.MuiMenu-root .MuiModal-backdrop',
        onNextClickEl: '.MuiMenu-root .MuiModal-backdrop',
      },
    ],
    '3D_VIEW_CONTAINER_TOUR': [
      {
        element: 'tbody > .MuiTableRow-root,.table-cards > div',
        popover: {
          title: i18n.t('tutorial.tours.containers.view3d1.title'),
          description: i18n.t('tutorial.tours.containers.view3d1.description'),
        },
      },
    ],
  },
  CONTAINERS_ADD: {
    NAME: i18n.t('tutorial.containersAddWithPreviousTimingTitle'),
    ROUTE: '/dashboard/containers/add',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.importExcel'),
        key: 'IMPORT_EXCEL_TOUR',
        showToRoles: 'admin',
      },
    ],
    IMPORT_EXCEL_TOUR: [
      {
        element: '#import-excel-row',
        popover: {
          title: i18n.t('tutorial.tours.containersAdd.importExcel1.title'),
          description: i18n.t('tutorial.tours.containersAdd.importExcel1.description'),
        },
      },
    ],
  },
  GATE_OUTS: {
    NAME: i18n.t('tutorial.gateOutsTitle'),
    ROUTE: '/dashboard/gateouts',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.tableWithRequestedFilters'),
        key: 'TABLE_WITH_REQUESTS_TOUR',
      },
    ],
    TABLE_WITH_REQUESTS_TOUR: [
      {
        element: '#table-with-requests',
        popover: {
          title: i18n.t('tutorial.tours.gateOuts.tableWithRequests1.title'),
          description: i18n.t('tutorial.tours.gateOuts.tableWithRequests1.description'),
        },
      },
    ],
  },
  GATE_OUTS_ADD: {
    NAME: i18n.t('tutorial.gateOutsAddTitle'),
    ROUTE: '/dashboard/gateouts/add',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.variants'),
        key: 'VARIANTS_TOUR',
      },
    ],
    VARIANTS_TOUR: [
      {
        element: '.select-create-mode',
        popover: {
          title: i18n.t('tutorial.tours.gateOutsAdd.variants1.title'),
          description: i18n.t('tutorial.tours.gateOutsAdd.variants1.description'),
        },
      },
    ],
  },
  REPAIRS: {
    NAME: i18n.t('tutorial.repairsTitle'),
    ROUTE: '/dashboard/repairs',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.basic'),
        key: 'BASIC_TOUR',
      },
      {
        name: i18n.t('button.createInvoiceByRepair'),
        key: 'CREATE_INVOICE_TOUR',
        showToRoles: 'admin',
      },
    ],
    BASIC_TOUR: [
      {
        element: 'tbody > .MuiTableRow-root',
        popover: {
          title: i18n.t('tutorial.tours.repairs.basic1.title'),
          description: i18n.t('tutorial.tours.repairs.basic1.description'),
        },
      },
      {
        element: '#column-active-count',
        popover: {
          title: i18n.t('tutorial.tours.repairs.basic2.title'),
          description: i18n.t('tutorial.tours.repairs.basic2.description'),
        },
        showToRoles: 'admin',
      },
      {
        element: '#column-pending-count',
        popover: {
          title: i18n.t('tutorial.tours.repairs.basic3.title'),
          description: i18n.t('tutorial.tours.repairs.basic3.description'),
        },
        showToRoles: 'client',
      },
    ],
    CREATE_INVOICE_TOUR: [
      {
        element: '#column-uncreated-invoice-count',
        popover: {
          title: i18n.t('tutorial.tours.repairs.createInvoice1.title'),
          description: i18n.t('tutorial.tours.repairs.createInvoice1.description'),
        },
      },
      {
        element: 'tbody .MuiCheckbox-root > input',
        popover: {
          title: i18n.t('tutorial.tours.repairs.createInvoice2.title'),
          description: i18n.t('tutorial.tours.repairs.createInvoice2.description'),
        },
      },
    ],
  },
  INVENTORY: {
    NAME: i18n.t('tutorial.inventoryTitle'),
    ROUTE: '/dashboard/inventory',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.note'),
        key: 'NOTE_TOUR',
        showToRoles: ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'],
      },
    ],
    NOTE_TOUR: [
      {
        element: '#inventory-page',
        popover: {
          title: i18n.t('tutorial.tours.inventory.note1.title'),
          description: i18n.t('tutorial.tours.inventory.note1.description'),
        },
      },
    ],
  },
  STORAGE_PRICINGS: {
    NAME: i18n.t('tutorial.storagePricingsTitle'),
    ROUTE: '/dashboard/storage-pricing',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.note'),
        key: 'NOTE_TOUR',
        showToRoles: 'admin',
      },
      {
        name: i18n.t('button.basic'),
        key: 'BASIC_TOUR',
        showToRoles: 'client',
      },
    ],
    NOTE_TOUR: [
      {
        element: '#storage-pricing-page',
        popover: {
          title: i18n.t('tutorial.tours.storagePricings.note1.title'),
          description: i18n.t('tutorial.tours.storagePricings.note1.description'),
        },
      },
    ],
    BASIC_TOUR: [
      {
        element: '#storage-pricing-page',
        popover: {
          title: i18n.t('tutorial.tours.storagePricings.basic1.title'),
          description: i18n.t('tutorial.tours.storagePricings.basic1.description'),
        },
      },
    ],
  },
  REPORTS: {
    NAME: i18n.t('tutorial.reportsTitle'),
    ROUTE: '/dashboard/reports',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.basic'),
        key: 'BASIC_TOUR',
        showToRoles: 'admin',
      },
    ],
    BASIC_TOUR: [
      {
        element: '#reports-filters',
        popover: {
          title: i18n.t('tutorial.tours.reports.basic1.title'),
          description: i18n.t('tutorial.tours.reports.basic1.description'),
        },
      },
      {
        element: '#show-reports-button',
        popover: {
          title: i18n.t('tutorial.tours.reports.basic2.title'),
          description: i18n.t('tutorial.tours.reports.basic2.description'),
        },
      },
    ],
  },
  INVOICES: {
    NAME: i18n.t('tutorial.invoicesTitle'),
    ROUTE: '/dashboard/invoices',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.basic'),
        key: 'BASIC_TOUR',
      },
      {
        name: i18n.t('button.print'),
        key: 'PRINT_TOUR',
      },
    ],
    BASIC_TOUR: [
      {
        element: '#invoices-by-client-yard-page',
        popover: {
          title: i18n.t('tutorial.tours.invoices.basic1.title'),
          description: i18n.t('tutorial.tours.invoices.basic1.description'),
        },
        showToRoles: 'admin',
      },
      {
        element: '#invoices-by-client-yard-page',
        popover: {
          title: i18n.t('tutorial.tours.invoices.basic1.title1'),
          description: i18n.t('tutorial.tours.invoices.basic1.description1'),
        },
        showToRoles: 'client',
      },
      {
        element: 'tbody',
        popover: {
          title: i18n.t('tutorial.tours.invoices.basic2.title'),
          description: i18n.t('tutorial.tours.invoices.basic2.description'),
        },
        showToRoles: 'admin',
      },
      {
        element: '#set-pending-button',
        popover: {
          title: i18n.t('tutorial.tours.invoices.basic3.title'),
          description: i18n.t('tutorial.tours.invoices.basic3.description'),
        },
        showToRoles: 'admin',
      },
    ],
    PRINT_TOUR: [
      {
        element: 'tbody',
        popover: {
          title: i18n.t('tutorial.tours.invoices.print1.title'),
          description: i18n.t('tutorial.tours.invoices.print1.description'),
        },
      },
    ],
  },
  TRADE_AND_SALES: {
    NAME: i18n.t('tutorial.tradeAndSalesTitle'),
    ROUTE: [
      '/dashboard/marketplace',
      '/dashboard/managesales',
      '/dashboard/cart',
      '/dashboard/sales-fees',
    ],
    BUTTON_TOURS: [
      {
        name: i18n.t('button.basic'),
        key: 'BASIC_TOUR',
        canTrade: true,
        showToRoles: [
          'SUPER_ADMIN',
          'YARD_COMPANY_ADMIN',
          'YARD_MANAGER',
          'CLIENT_MANAGER',
          'CLIENT_DISPATCHER',
        ],
      },
    ],
    BASIC_TOUR: [
      {
        element: '#drawer-button-marketplace',
        popover: {
          title: i18n.t('tutorial.tours.tradeAndSales.basic1.title'),
          description: i18n.t('tutorial.tours.tradeAndSales.basic1.description'),
        },
      },
      {
        element: '#drawer-button-managesales',
        popover: {
          title: i18n.t('tutorial.tours.tradeAndSales.basic2.title'),
          description: i18n.t('tutorial.tours.tradeAndSales.basic2.description'),
        },
      },
      {
        element: '#drawer-button-cart',
        popover: {
          title: i18n.t('tutorial.tours.tradeAndSales.basic3.title'),
          description: i18n.t('tutorial.tours.tradeAndSales.basic3.description'),
        },
      },
      {
        element: '#drawer-button-sales-fees',
        popover: {
          title: i18n.t('tutorial.tours.tradeAndSales.basic4.title'),
          description: i18n.t('tutorial.tours.tradeAndSales.basic4.description'),
        },
        showToRoles: 'admin',
      },
    ],
  },
  DEPOT_COMPANIES: {
    NAME: i18n.t('tutorial.depotCompaniesTitle'),
    ROUTE: '/dashboard/yardcompanies',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.basic'),
        key: 'BASIC_TOUR',
        showToRoles: ['SUPER_ADMIN'],
      },
    ],
    BASIC_TOUR: [
      {
        element: '#yardcompanies-page',
        popover: {
          title: i18n.t('tutorial.tours.depotCompanies.basic1.title'),
          description: i18n.t('tutorial.tours.depotCompanies.basic1.description'),
        },
      },
    ],
  },
  DEPOTS: {
    NAME: i18n.t('tutorial.depotsTitle'),
    ROUTE: '/dashboard/yards',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.edit'),
        key: 'EDIT_TOUR',
        showToRoles: ['SUPER_ADMIN'],
      },
      {
        name: i18n.t('button.clientDepotConnection'),
        key: 'CLIENT_DEPOT_CONNECTION_TOUR',
        showToRoles: ['SUPER_ADMIN'],
      },
    ],
    EDIT_TOUR: [
      {
        element: 'tbody > .MuiTableRow-root',
        popover: {
          title: i18n.t('tutorial.tours.depots.edit1.title'),
          description: i18n.t('tutorial.tours.depots.edit1.description'),
        },
      },
    ],
    CLIENT_DEPOT_CONNECTION_TOUR: [
      {
        element: '.MuiTableCell-root .MuiIconButton-root',
        popover: {
          title: i18n.t('tutorial.tours.depots.clientDepotConnection1.title'),
          description: i18n.t('tutorial.tours.depots.clientDepotConnection1.description'),
        },
      },
    ],
  },
  CLIENT_COMPANIES: {
    NAME: i18n.t('tutorial.clientCompaniesTitle'),
    ROUTE: '/dashboard/clients',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.edit'),
        key: 'EDIT_TOUR',
        showToRoles: 'admin',
      },
      {
        name: i18n.t('button.clientDepotConnection'),
        key: 'CLIENT_DEPOT_CONNECTION_TOUR',
        showToRoles: ['SUPER_ADMIN'],
      },
    ],
    EDIT_TOUR: [
      {
        element: 'tbody > .MuiTableRow-root',
        popover: {
          title: i18n.t('tutorial.tours.clientCompanies.edit1.title'),
          description: i18n.t('tutorial.tours.clientCompanies.edit1.description'),
        },
      },
    ],
    CLIENT_DEPOT_CONNECTION_TOUR: [
      {
        element: '.MuiTableCell-root .MuiIconButton-root',
        popover: {
          title: i18n.t('tutorial.tours.clientCompanies.clientDepotConnection1.title'),
          description: i18n.t('tutorial.tours.clientCompanies.clientDepotConnection1.description'),
        },
      },
    ],
  },
  EMPLOYEES: {
    NAME: i18n.t('tutorial.employeesTitle'),
    ROUTE: '/dashboard/employees',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.changeAccountStatus'),
        key: 'CHANGE_ACCOUNT_STATUS_TOUR',
        showToRoles: ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER', 'CLIENT_MANAGER'],
      },
      {
        name: i18n.t('button.changePermissions'),
        key: 'CHANGE_PERMISSIONS_TOUR',
        showToRoles: ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'],
      },
    ],
    CHANGE_ACCOUNT_STATUS_TOUR: [
      {
        element: '.MuiTableCell-root .MuiIconButton-root',
        popover: {
          title: i18n.t('tutorial.tours.employees.changeAccountStatus1.title'),
          description: i18n.t('tutorial.tours.employees.changeAccountStatus1.description'),
        },
      },
    ],
    CHANGE_PERMISSIONS_TOUR: [
      {
        element: '#column-role',
        popover: {
          title: i18n.t('tutorial.tours.employees.changePermissions1.title'),
          description: i18n.t('tutorial.tours.employees.changePermissions1.description'),
        },
      },
      {
        element: '.MuiTableCell-root .MuiIconButton-root',
        popover: {
          title: i18n.t('tutorial.tours.employees.changePermissions2.title'),
          description: i18n.t('tutorial.tours.employees.changePermissions2.description'),
        },
      },
    ],
  },
  ACCOUNT: {
    NAME: i18n.t('tutorial.accountTitle'),
    ROUTE: '/dashboard/account',
    BUTTON_TOURS: [
      {
        name: i18n.t('button.updatePassword'),
        key: 'UPDATE_PASSWORD_TOUR',
      },
    ],
    UPDATE_PASSWORD_TOUR: [
      {
        element: '#update-password-button',
        popover: {
          title: i18n.t('tutorial.tours.account.updatePassword1.title'),
          description: i18n.t('tutorial.tours.account.updatePassword1.description'),
        },
      },
    ],
  },
};

const resolveTutorialElement = (element?: string | (() => Element)) => {
  if (typeof element !== 'string' || !element.includes(',')) return element;

  return () => queryFirstMatchingSelector(element);
};

const normalizeSteps = (steps: unknown, user: User, isSmallScreen: boolean) => {
  if (!Array.isArray(steps)) return steps;

  return steps
    .map((step) => {
      if (!step || typeof step !== 'object') return step;

      const tourStep = step as TourStep;
      if (!tourStep.popover || typeof tourStep.popover !== 'object') return step;

      return {
        ...tourStep,
        element: resolveTutorialElement(tourStep.element),
        popover: {
          ...tourStep.popover,
          side: tourStep.popover.side ?? 'bottom',
        },
      };
    })
    .filter((tour) => !tour.showToRoles || showToRoles(tour.showToRoles, user))
    .filter(
      (step) => !step.showToScreen || step.showToScreen === (isSmallScreen ? 'mobile' : 'desktop'),
    );
};

const normalizeTutorialPage = (
  page: TutorialPageConfig,
  user: User,
  isSmallScreen: boolean,
): TutorialPageConfig => {
  const normalized: TutorialPageConfig = {
    ...page,
    BUTTON_TOURS: page.BUTTON_TOURS.filter(
      (tour) => !tour.showToRoles || showToRoles(tour.showToRoles, user),
    )
      .filter(
        (tour) =>
          !tour.showToScreen || tour.showToScreen === (isSmallScreen ? 'mobile' : 'desktop'),
      )
      .filter((tour) =>
        typeof tour.canTrade === 'boolean' ? tour.canTrade === user.canTrade : true,
      ),
  };

  Object.keys(page).forEach((key) => {
    if (key === 'NAME' || key === 'ROUTE' || key === 'BUTTON_TOURS') return;

    const value = page[key];
    if (Array.isArray(value)) {
      normalized[key] = normalizeSteps(value, user, isSmallScreen);
    }
  });

  return normalized;
};

export const TUTORIALS = (user: User, isSmallScreen: boolean) => {
  const pages = Object.entries(TUTORIALS_DATA).map(([pageKey, page]) => ({
    pageKey,
    page: normalizeTutorialPage(page, user, isSmallScreen),
  }));

  return Object.fromEntries(
    pages
      .filter(({ page }) => page.BUTTON_TOURS.length > 0)
      .map(({ pageKey, page }) => [pageKey, page]),
  );
};
